import { getLabelFormatBySeriesNumber } from "src/components/Charts/Simple/echarts/BarChart/labels";
import { FUNNEL, PIE, PIE_GRADE, TREE_MAP, SIMPLE_BUBBLE, GRAPH_BUBBLE } from "src/components/Charts/types";
import { CORRELATION_CARD } from "src/sections/GroupCards/cardTypes";

/**
 * Evita o reprocessamento e recálculo de todo o objeto de configuração
 * dos gráficos quando somente a propriedade de cores do gráfico muda
 * aplicando uma estratégia de destructuring do objeto atual e mudando somente
 * a propriedade de cor que sofreu alteração com o seu novo valor.
 * @param {object} options Objeto de opções que serão aplicados ao gráfico 
 * @returns Objeto de opções completo "options" e objeto de configuração
 * a ser consumido pelo echarts "optionsToEcharts"
 */
const updateOnlyColors = function (options = {}) {
  const { currentOptions, colors: theme } = options;
  return {
    options: {
      ...currentOptions,
      color: theme.colors,
    },
    optionsToEcharts: {
      color: theme.colors,
    },
  };
};

/**
 * Evita o reprocessamento e recálculo de todo o objeto de configuração
 * dos gráficos quando somente a propriedade o índice do gráfico clicado muda
 * aplicando uma estratégia de destructuring do objeto atual e mudando somente
 * a propriedade onde highlitIndex é consumido.
 * @param {object} options Objeto de opções que serão aplicados ao gráfico 
 * @returns Objeto de opções completo "options", este caso não retorna "echartsToOptions"
 * por não ser possível fazer essa atualização dinâmica diretamente no método .setOption do echarts.
 */
const updateOnlyHighlitIndex = function (options = {}) {
  const { currentOptions, colors: theme, highlitIndex, identifier } = options;

  const styleObject = {
    itemStyle: {
      borderType: theme.highlitedItemBorderType,
      borderColor: theme.highlitedItemBorderColor,
      borderWidth: theme.highlitedItemBorderWidth,
    },
  };

  if (
    [PIE, FUNNEL, PIE_GRADE].some((substring) => identifier.includes(substring))
  ) {
    const echartOptions = {
      ...currentOptions,
      series: currentOptions.series.map((serie) => ({
        ...serie,
        data: serie.data.map((dataItem, dataItemIndex) => ({
          ...dataItem,
          ...(highlitIndex === dataItemIndex
            ? styleObject
            : { itemStyle: null }),
        })),
      })),
    };
    return {
      options: echartOptions,
    };
  }

  const echartOptions = {
    ...currentOptions,
    series: currentOptions.series.map((serie, serieIndex) => ({
      ...serie,
      ...(serieIndex === highlitIndex ? styleObject : { itemStyle: null }),
    })),
  };

  return {
    options: echartOptions,
  };
};

/**
 * Evita o reprocessamento e recálculo de todo o objeto de configuração
 * dos gráficos quando somente a propriedade de fontes muda
 * aplicando uma estratégia de destructuring do objeto atual e mudando somente
 * a propriedade chartFontSize.
 * @param {object} options Objeto de opções que serão aplicados ao gráfico 
 * @returns Objeto de opções completo "options" e objeto de configuração
 * a ser consumido pelo echarts "optionsToEcharts"
 */
const updateOnlyFontSize = function(options = {}) {
  const { currentOptions, chartFontSize } = options;
  
  const textStyle = {
    fontSize: chartFontSize,
  };

  return {
    options: {
      ...currentOptions,
      legend: {
        ...currentOptions.legend,
        textStyle: {
          ...currentOptions.legend.textStyle,
          ...textStyle,
        }
      },
      series: currentOptions.series.map((serie) => ({
        ...serie,
        label: {
          ...serie.label,
          textStyle: {
            ...serie.label.textStyle,
            ...textStyle,
          }
        }
      }))
    },
    optionsToEcharts: {
      legend: {
        textStyle: {
          ...textStyle,
        }
      },
      series: {
        label: {
          textStyle: {
            ...textStyle,
          }
        }
      }
    }
  };
};

/**
 * Evita o reprocessamento e recálculo de todo o objeto de configuração
 * dos gráficos ao detectar que o gráfico deve aplicar o método de "resizing",
 * que é quando o comprimento e a altura do gráfico mudam e ele precisa atualizar
 * seus formatadores de label para se adequar as novas dimensões.
 * @param {object} options Objeto de opções que serão aplicados ao gráfico 
 * @returns Objeto de opções completo "options" e objeto de configuração
 * a ser consumido pelo echarts "optionsToEcharts"
 */
const updateOnlyLabelFormatter = function(options = {}) {
  const { currentOptions, disabledLegends, chartSize } = options;

  const labelFormatter = getLabelFormatBySeriesNumber(
    currentOptions.series.length - disabledLegends.length,
    chartSize,
    true,
    0,
    false,
  );

  const newSeries = currentOptions.series.map((serieItem) => ({
    ...serieItem,
    label: {
      ...serieItem.label,
      formatter: labelFormatter,
    }
  }));

  return {
    options: {
      ...currentOptions,
      series: newSeries,
    },
    optionsToEcharts: {
      series: newSeries,
    },
  };
};

/**
 * Pinta os nós de pergunta (level 3) e seus filhos (level 4) com a cor correspondente
 * vinda do array `theme.colors`, mantendo a ordem alinhada a `currentOptions.legend.data`.
 * Os filhos sofrem `colorSaturation` definido em `levels` do treemap, gerando os tons.
 */
function repaintTreeMapByLegendOrder(rootNodes, legends, themeColors) {
  if (!Array.isArray(rootNodes)) return rootNodes;
  return rootNodes.map((alternativaPrincipal) => ({
    ...alternativaPrincipal,
    children: Array.isArray(alternativaPrincipal.children)
      ? alternativaPrincipal.children.map((pergunta) => {
        const idx = legends.indexOf(pergunta.path);
        const color = idx >= 0 ? themeColors[idx] : (pergunta.itemStyle && pergunta.itemStyle.color);
        if (!color) return pergunta;
        return {
          ...pergunta,
          itemStyle: { ...(pergunta.itemStyle || {}), color },
          children: Array.isArray(pergunta.children)
            ? pergunta.children.map((alt) => ({
              ...alt,
              itemStyle: { ...(alt.itemStyle || {}), color },
            }))
            : pergunta.children,
        };
      })
      : alternativaPrincipal.children,
  }));
}

/**
 * Override de `updateOnly:colors` para o treemap — em vez de só setar `option.color`,
 * reconstrói os dados das séries aplicando a nova cor por nó de pergunta correlacionada
 * (e a propaga aos filhos para receber a saturação definida em `levels`).
 */
const updateOnlyColorsForTreeMap = function (options = {}) {
  const { currentOptions, colors: theme } = options;
  const themeColors = (theme && theme.colors) || [];
  const legends = (currentOptions && currentOptions.legend && currentOptions.legend.data) || [];

  const newSeries = (currentOptions.series || []).map((serie) => ({
    ...serie,
    data: repaintTreeMapByLegendOrder(serie.data, legends, themeColors),
  }));

  return {
    options: { ...currentOptions, color: themeColors, series: newSeries },
    optionsToEcharts: { color: themeColors, series: newSeries },
  };
};

/**
 * Override de `updateOnly:highlitIndex` para o treemap — aplica bordas de destaque no nó
 * da pergunta clicada e em todos os seus filhos (alternativas correlacionadas), como
 * pedido para a UX de "todos os itens da pergunta destacados".
 */
const updateOnlyHighlitIndexForTreeMap = function (options = {}) {
  const { currentOptions, highlitIndex, colors: theme } = options;
  const legends = (currentOptions && currentOptions.legend && currentOptions.legend.data) || [];
  const targetEnunciado = typeof highlitIndex === 'number' && highlitIndex >= 0 ? legends[highlitIndex] : null;

  const highlitStyle = {
    borderType: theme && theme.highlitedItemBorderType,
    borderColor: theme && theme.highlitedItemBorderColor,
    borderWidth: theme && theme.highlitedItemBorderWidth,
  };

  function paint(node) {
    const isPergunta = node && legends.includes(node.path);
    if (isPergunta) {
      const isMatch = node.path === targetEnunciado;
      const updatedNode = {
        ...node,
        itemStyle: { ...(node.itemStyle || {}), ...(isMatch ? highlitStyle : { borderColor: undefined, borderWidth: undefined, borderType: undefined }) },
      };
      if (Array.isArray(node.children)) {
        updatedNode.children = node.children.map((child) => ({
          ...child,
          itemStyle: { ...(child.itemStyle || {}), ...(isMatch ? highlitStyle : { borderColor: undefined, borderWidth: undefined, borderType: undefined }) },
        }));
      }
      return updatedNode;
    }
    if (Array.isArray(node.children)) {
      return { ...node, children: node.children.map(paint) };
    }
    return node;
  }

  const newSeries = (currentOptions.series || []).map((serie) => ({
    ...serie,
    data: Array.isArray(serie.data) ? serie.data.map(paint) : serie.data,
  }));

  return {
    options: { ...currentOptions, series: newSeries },
    optionsToEcharts: { series: newSeries },
  };
};

/**
 * Override de `updateOnly:fontSize` para o simple_bubble — atualiza o `yAxis.axisLabel`
 * (eixo Y visível) e a `legend.textStyle` (legenda centralizada no topo do gráfico).
 * Não usa o default porque as séries scatter não têm `serie.label` definido.
 */
const updateOnlyFontSizeForSimpleBubble = function (options = {}) {
  const { currentOptions, chartFontSize } = options;
  const yAxis = {
    ...currentOptions.yAxis,
    axisLabel: {
      ...(currentOptions.yAxis && currentOptions.yAxis.axisLabel),
      textStyle: {
        ...(currentOptions.yAxis && currentOptions.yAxis.axisLabel && currentOptions.yAxis.axisLabel.textStyle),
        fontSize: chartFontSize,
      },
    },
  };

  const legend = {
    ...currentOptions.legend,
    textStyle: {
      ...(currentOptions.legend && currentOptions.legend.textStyle),
      fontSize: chartFontSize,
    },
  };

  return {
    options: { ...currentOptions, yAxis, legend },
    optionsToEcharts: { yAxis, legend },
  };
};

/**
 * Override de `updateOnly:fontSize` para o graph_bubble — escreve `fontSize` direto em
 * `series.label` (forma idiomática do echarts atual) e na `legend.textStyle` ao invés
 * do `label.textStyle` legado gerado pela strategy default.
 */
const updateOnlyFontSizeForGraphBubble = function (options = {}) {
  const { currentOptions, chartFontSize } = options;
  const newSeries = (currentOptions.series || []).map((serie) => ({
    ...serie,
    label: { ...(serie.label || {}), fontSize: chartFontSize },
  }));
  const newLegend = {
    ...currentOptions.legend,
    textStyle: {
      ...(currentOptions.legend && currentOptions.legend.textStyle),
      fontSize: chartFontSize,
    },
  };

  return {
    options: { ...currentOptions, series: newSeries, legend: newLegend },
    optionsToEcharts: { series: newSeries, legend: newLegend },
  };
};

/**
 * Override de `updateOnly:colors` para o graph_bubble — substitui o array de cores
 * diretamente, já que as categorias do echart estão remapeadas para 0..N-1 alinhadas
 * com `legend.data` (a pergunta principal não consome slot — é pintada pelo
 * `itemStyle.color` fixo definido no `toEchartOption`).
 */
const updateOnlyColorsForGraphBubble = function (options = {}) {
  const { currentOptions, colors: theme } = options;
  const themeColors = (theme && theme.colors) || [];

  const newSeries = (currentOptions.series || []).map((serie) => ({
    ...serie,
    color: themeColors,
  }));

  return {
    options: { ...currentOptions, color: themeColors, series: newSeries },
    optionsToEcharts: { color: themeColors, series: newSeries },
  };
};

/**
 * Override de `updateOnly:highlitIndex` para o graph_bubble — aplica borda nos nodes
 * cuja `category` corresponde à pergunta highlightada. Como o remap deixou as
 * correlacionadas em 0..N-1, `targetCategory === highlitIndex` direto.
 */
const updateOnlyHighlitIndexForGraphBubble = function (options = {}) {
  const { currentOptions, colors: theme, highlitIndex } = options;
  const targetCategory = typeof highlitIndex === 'number' && highlitIndex >= 0 ? highlitIndex : -1;

  const borderStyle = {
    borderType: theme && theme.highlitedItemBorderType,
    borderColor: theme && theme.highlitedItemBorderColor,
    borderWidth: theme && theme.highlitedItemBorderWidth,
  };

  const newSeries = (currentOptions.series || []).map((serie) => ({
    ...serie,
    data: Array.isArray(serie.data) ? serie.data.map((node) => {
      const nodeCategory = typeof node.category === 'number' ? node.category : -1;
      if (nodeCategory !== targetCategory) {
        // Limpa apenas a borda residual sem perder a cor de fundo da bolha (ex: mainColor da principal).
        if (node.itemStyle && (node.itemStyle.borderColor || node.itemStyle.borderWidth || node.itemStyle.borderType)) {
          const { borderType, borderColor, borderWidth, ...keep } = node.itemStyle;
          return { ...node, itemStyle: keep };
        }
        return node;
      }

      /**
       * Evitando highlit em caso de nó raíz
       */
      if (typeof node.parent === "undefined") {
        return {
          ...node
        }
      }

      return {
        ...node,
        itemStyle: { ...(node.itemStyle || {}), ...borderStyle },
      };
    }) : serie.data,
  }));

  return {
    options: { ...currentOptions, series: newSeries },
    optionsToEcharts: { series: newSeries },
  };
};

// Estratégias por identifier — quando presentes, sobrescrevem o comportamento padrão de
// `updateOnly:<key>` para um identifier específico. Útil para tipos de gráfico cuja
// atualização "leve" diverge da padrão (ex: treemap, bubble — não usam `options.color` linear).
const updateOnlyStrategiesByIdentifier = {
  [`${CORRELATION_CARD}_${TREE_MAP}`]: {
    colors: updateOnlyColorsForTreeMap,
    highlitIndex: updateOnlyHighlitIndexForTreeMap,
  },
  [`${CORRELATION_CARD}_${SIMPLE_BUBBLE}`]: {
    // colors e highlitIndex usam o pipeline padrão: como o simple_bubble agora tem
    // uma série por pergunta, `option.color[seriesIndex]` repinta os bubbles e o
    // `itemStyle` no nível da série propaga o border destacado.
    fontSize: updateOnlyFontSizeForSimpleBubble,
  },
  [`${CORRELATION_CARD}_${GRAPH_BUBBLE}`]: {
    colors: updateOnlyColorsForGraphBubble,
    highlitIndex: updateOnlyHighlitIndexForGraphBubble,
    fontSize: updateOnlyFontSizeForGraphBubble,
  },
};

// Estratégias de obtenção das opções que serão consumidas pelo gráfico.
const updateOnlyStrategies = {
  colors: updateOnlyColors,
  highlitIndex: updateOnlyHighlitIndex,
  fontSize: updateOnlyFontSize,
  resizing: updateOnlyLabelFormatter,
};

/**
 * Calcula um objeto de opções que será consumido pelo echartOption através
 * de uma estratégia de atualização única, evitando todo o reprocessamento
 * e recálculo do objeto de configuração inteiro do gráfico.
 * @param {object} options Objeto de opções que serão aplicados ao gráfico 
 * @param {string} updateOnly Identificador da propriedade que será atualizada 
 * @returns Objeto de configuração que será consumido pelo echarts.
 */
export const updateOnlyStrategyResolver = function (options, updateOnly) {
  const { identifier } = options || {};
  // Override por identifier — quando um tipo de gráfico precisa de comportamento próprio
  // para a atualização leve (ex: treemap, que pinta nó a nó), tem prioridade sobre o default.
  const perIdentifier = identifier && updateOnlyStrategiesByIdentifier[identifier];
  if (perIdentifier && typeof perIdentifier[updateOnly] === "function") {
    return perIdentifier[updateOnly](options);
  }
  if (typeof updateOnlyStrategies[updateOnly] === "function") {
    return updateOnlyStrategies[updateOnly](options);
  }
};